import { MessageDialog, useDialogManager } from '../../../../../../widgets/dialog';
import { showErrorNotification, showSuccessNotification } from '../../../../../../widgets/notificationToaster';
import usePreview3DStore from 'src/components/widgets/preview3D/store';
import { dataURLtoFile, stringFormat, T, uploadComposerPreviewImage } from 'src/shared/helpers';
import CameraIcon from 'src/shared/material_icons/image/photo_camera.svg';
import UploadIcon from 'src/shared/material_icons/file/upload.svg';
import { MaterialsIcon, TrashIcon } from 'src/components/widgets/icons/scene';
import useComposerStore from '../../../configurator-composer/composer/composerStore';
import { ComposerAttributeMark } from '../../../configurator-composer/composer/components/composerAttributeTreeElement';
import { createSecureClonedName } from '../../../configurator-composer/composer/helpers/composerHelpers';
import {
    applyComposerOptionImage,
    ComposerOptionImageReference,
    createComposerOptionImageReference
} from '../../../configurator-composer/composer/helpers/composerOptionImage';
import { ComposerPickerEntry, useComposerPicker } from '../composerPickerMenu';
import { ComposerAttributeNode, ComposerAttributeOptionNode, ComposerAttributeType } from './composerAttributesModels';

// «1 option», «3 options»: il numero e la parola giusta, in un posto solo.
const optionCount = (count: number) => stringFormat(count === 1 ? T._('{0} option', 'Admin') : T._('{0} options', 'Admin'), count.toString());

// I COMANDI SULLE OPZIONI SCELTE, SENZA FINESTRE (utente, 2026-10-15). Copiare le opzioni in un altro
// attributo e dare loro un'immagine di anteprima erano due tasti nella barra dell'albero, ognuno con la
// sua finestra che chiedeva di nuovo che cosa — l'attributo sorgente, le opzioni, un filtro per nome.
// Ora il «che cosa» è la selezione dell'albero, e la finestra resta solo dove si sostituisce qualcosa.

type ImageSource = { kind: 'object' } | { kind: 'material'; material: BABYLON.Material } | { kind: 'upload'; file: File } | { kind: 'remove' };

const isOptionAttribute = (attribute: ComposerAttributeNode) =>
    attribute.type === ComposerAttributeType.Attribute && attribute.attributeType !== MPlaza.AttributeType.CustomizableArea;

/** Le opzioni di una selezione di attributi: quelle degli attributi scelti e di quelli dentro i gruppi scelti. */
export const optionsOfAttributes = (attributes: ComposerAttributeNode[]) => {
    const all = useComposerStore.getState().attributes;
    const ids = new Set<string>();
    const collect = (id: string) => {
        if (ids.has(id)) return;
        ids.add(id);
        all.filter((candidate) => candidate.parentID === id).forEach((child) => collect(child.id));
    };
    attributes.forEach((attribute) => collect(attribute.id));
    return all.filter((attribute) => ids.has(attribute.id) && isOptionAttribute(attribute)).flatMap((attribute) => attribute.options);
};

// La selezione dello store può essere di un attimo fa: si lavora sempre sulle opzioni come sono adesso.
const freshOptions = (options: ComposerAttributeOptionNode[]) => {
    const current = useComposerStore.getState().attributes.flatMap((attribute) => attribute.options);
    return options.map((option) => current.find((candidate) => candidate.id === option.id) ?? option);
};

export const useOptionSelectionCommands = () => {
    const { showDialog, closeDialog } = useDialogManager();
    const { openPicker, picker } = useComposerPicker();
    const [updateAttribute, validateAttributes, sceneMaterials] = useComposerStore((store) => [
        store.updateAttribute,
        store.validateAttributes,
        store.sceneMaterialList
    ]);
    const sceneEditor = usePreview3DStore((store) => store.sceneEditor);

    // COPIA: la stessa ricetta della finestra di prima — codici resi unici, azioni e prezzi copiati, la
    // prima opzione predefinita se la destinazione era vuota. «Replace» toglie prima quelle che c'erano.
    const copyOptions = (selected: ComposerAttributeOptionNode[], targetID: string, replace: boolean) => {
        const state = useComposerStore.getState();
        const options = freshOptions(selected);
        const target = state.attributes.find((attribute) => attribute.id === targetID);
        if (!target || !options.length) return;
        const existingOptions = replace ? [] : [...(target.options || [])];
        const usedCodes = existingOptions.map((option) => option.code);
        const copiedOptions = options.map((option, index) => {
            const optionID = MPlaza.generateUUID();
            const code = createSecureClonedName(option.code, usedCodes);
            usedCodes.push(code);
            return {
                ...option,
                id: optionID,
                idNumeric: -1,
                code,
                attributeID: target.id,
                displayOrder: existingOptions.length + index,
                isDefault: existingOptions.length === 0 && index === 0,
                isNew: true,
                actions: option.actions.map((action, actionIndex) => ({
                    ...action,
                    id: MPlaza.generateUUID(),
                    attributeID: target.id,
                    optionID,
                    displayOrder: actionIndex,
                    isNew: true
                }))
            };
        });
        const copiedPricings = options.flatMap((option, index) => {
            const source = state.attributes.find((attribute) => attribute.id === option.attributeID);
            return (source?.pricings || [])
                .filter((pricing) => pricing.selectedOptionGuid === option.id)
                .map((pricing) => ({ ...pricing, attributePricingID: 0, selectedOptionID: -1, selectedOptionGuid: copiedOptions[index].id }));
        });
        updateAttribute({
            ...target,
            options: [...existingOptions, ...copiedOptions],
            pricings: replace ? copiedPricings : [...target.pricings, ...copiedPricings]
        });
        void validateAttributes();
        showSuccessNotification({
            message: stringFormat(
                replace ? T._('{0} of {1} replaced by the copied ones.', 'Admin') : T._('Copied {0} to {1}.', 'Admin'),
                optionCount(replace ? (target.options?.length ?? 0) : options.length),
                target.name
            )
        });
    };

    // SOSTITUIRE TOGLIE OPZIONI: si chiede prima (utente, 2026-10-15). Copiare aggiunge e basta.
    const confirmReplace = (options: ComposerAttributeOptionNode[], target: ComposerAttributeNode) => {
        showDialog(
            'confirmDialog',
            <MessageDialog
                title={stringFormat(T._('Replace the options of {0}?', 'Admin'), target.name)}
                message={stringFormat(
                    T._('{0} of {1} will be removed and replaced by the {2} copied.', 'Admin'),
                    optionCount(target.options?.length ?? 0),
                    target.name,
                    optionCount(options.length)
                )}
                buttons={[
                    {
                        label: T._('Replace', 'Admin'),
                        destructive: true,
                        onClick: () => {
                            closeDialog('confirmDialog');
                            copyOptions(options, target.id, true);
                        }
                    }
                ]}
            />
        );
    };

    /** «Copy to» e «Replace in»: l'elenco degli attributi a opzioni, senza quello da cui vengono tutte le scelte. */
    const openCopyPicker = (anchor: HTMLElement | null | undefined, options: ComposerAttributeOptionNode[], mode: 'copy' | 'replace') => {
        const sources = new Set(options.map((option) => option.attributeID));
        const targets = useComposerStore
            .getState()
            .attributes.filter((attribute) => isOptionAttribute(attribute) && !(sources.size === 1 && sources.has(attribute.id)));
        const entries: ComposerPickerEntry[] = targets.map((target) => ({
            id: target.id,
            label: target.name,
            caption: optionCount(target.options?.length ?? 0),
            icon: <ComposerAttributeMark attribute={target} />,
            onPick: () => (mode === 'copy' ? copyOptions(options, target.id, false) : confirmReplace(options, target))
        }));
        openPicker(anchor, entries, mode === 'copy' ? T._('Copy to', 'Admin') : T._('Replace the options of', 'Admin'));
    };

    // L'IMMAGINE DI ANTEPRIMA: le stesse fonti della finestra di prima — la scena com'è, un materiale, un
    // file — più «Remove image». Si carica una volta e si dà a tutte le opzioni scelte.
    const applyImage = async (options: ComposerAttributeOptionNode[], source: ImageSource) => {
        try {
            let image: ComposerOptionImageReference | null = null;
            if (source.kind === 'upload') {
                const uploaded = await uploadComposerPreviewImage(source.file);
                image = createComposerOptionImageReference(uploaded.url, uploaded.key);
            } else if (source.kind !== 'remove') {
                if (!sceneEditor) throw new Error('The 3D editor is not ready.');
                const imageData =
                    source.kind === 'material'
                        ? await sceneEditor.getMaterialScreenshotAsync(source.material.id, 512, 512)
                        : await sceneEditor.getSceneContentScreenshotAsync(512, 512);
                if (!imageData) throw new Error('The 3D editor did not return an image.');
                const uploaded = await uploadComposerPreviewImage(dataURLtoFile(imageData, 'composer-options-preview.png'));
                image = createComposerOptionImageReference(uploaded.url, uploaded.key);
            }
            const ids = new Set(options.map((option) => option.id));
            useComposerStore
                .getState()
                .attributes.filter((attribute) => attribute.options.some((option) => ids.has(option.id)))
                .forEach((attribute) =>
                    updateAttribute({
                        ...attribute,
                        options: attribute.options.map((option) => (ids.has(option.id) ? applyComposerOptionImage(option, image) : option))
                    })
                );
            await validateAttributes();
            showSuccessNotification({
                message: stringFormat(
                    source.kind === 'remove' ? T._('Image removed from {0}.', 'Admin') : T._('Image applied to {0}.', 'Admin'),
                    optionCount(options.length)
                )
            });
        } catch (error) {
            console.error('Unable to set the Composer option image:', error);
            showErrorNotification({ message: T._('The image could not be applied. Check the 3D scene and try again.', 'Admin') });
        }
    };

    const pickFile = (options: ComposerAttributeOptionNode[]) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/png,image/jpeg,image/webp';
        input.onchange = () => {
            const file = input.files?.[0];
            if (file) void applyImage(options, { kind: 'upload', file });
        };
        input.click();
    };

    /** «Preview image»: le fonti; «Use material…» apre al suo posto l'elenco dei materiali della scena. */
    const openImagePicker = (anchor: HTMLElement | null | undefined, selected: ComposerAttributeOptionNode[]) => {
        const options = freshOptions(selected);
        if (!options.length) return;
        const materials = sceneMaterials.filter((material) => material.id !== 'default material' && material.name !== 'default material');
        openPicker(
            anchor,
            [
                {
                    id: 'object',
                    label: T._('Use object', 'Admin'),
                    caption: T._('The 3D scene as it is shown now', 'Admin'),
                    icon: <CameraIcon />,
                    disabled: !sceneEditor,
                    onPick: () => void applyImage(options, { kind: 'object' })
                },
                {
                    id: 'material',
                    label: T._('Use material…', 'Admin'),
                    caption: T._('A material of the scene', 'Admin'),
                    icon: <MaterialsIcon />,
                    disabled: !sceneEditor || materials.length === 0,
                    onPick: () =>
                        openPicker(
                            anchor,
                            materials.map((material) => ({
                                id: material.id,
                                label: material.name || material.id,
                                icon: <MaterialsIcon />,
                                onPick: () => void applyImage(options, { kind: 'material', material })
                            })),
                            T._('Use material', 'Admin')
                        )
                },
                {
                    id: 'upload',
                    label: T._('Upload…', 'Admin'),
                    caption: T._('PNG, JPG, JPEG, WEBP', 'Admin'),
                    icon: <UploadIcon />,
                    onPick: () => pickFile(options)
                },
                {
                    id: 'remove',
                    label: T._('Remove image', 'Admin'),
                    icon: <TrashIcon />,
                    danger: true,
                    disabled: !options.some((option) => option.img),
                    onPick: () => void applyImage(options, { kind: 'remove' })
                }
            ],
            stringFormat(T._('Preview image for {0}', 'Admin'), optionCount(options.length))
        );
    };

    return { openCopyPicker, openImagePicker, picker };
};
